import React, { useEffect, useState } from "react";
import { Loader2, Save, FileText, Building2, AlertCircle, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BusinessInfoCard — admin form to edit Big Kreation Entertainment Pvt Ltd's
 * legal/tax fields used on every GST tax invoice.
 *
 * The values live in `db.system_config.business_info` (server-side); this
 * component is a thin form that reads/writes via the admin API.
 */

const FIELDS = [
  { k: "company_name", label: "Legal company name", placeholder: "Big Kreation Entertainment Pvt Ltd", required: true },
  { k: "brand_name", label: "Brand name (with ®)", placeholder: "DoozyGo®", required: true },
  { k: "gstin", label: "GSTIN (15-char)", placeholder: "27AABCB1234M1ZK", maxLength: 15 },
  { k: "pan", label: "PAN (10-char)", placeholder: "AABCB1234M", maxLength: 10 },
  { k: "cin", label: "CIN (Pvt Ltd)", placeholder: "U74999MH2020PTC123456", maxLength: 21 },
  { k: "registered_address", label: "Registered office address", placeholder: "Building, street, area, city", textarea: true },
  { k: "state", label: "State", placeholder: "Maharashtra" },
  { k: "state_code", label: "GST state code (2-digit)", placeholder: "27", maxLength: 2 },
  { k: "pincode", label: "PIN code", placeholder: "400053", maxLength: 6 },
  { k: "support_email", label: "Customer support email", placeholder: "support@doozygo.com" },
  { k: "support_phone", label: "Customer support phone", placeholder: "+91-XXXXXXXXXX" },
  { k: "authorized_signatory_name", label: "Authorized signatory", placeholder: "Sumit Sharma" },
  { k: "authorized_signatory_title", label: "Signatory title", placeholder: "Director" },
  { k: "invoice_prefix", label: "Invoice number prefix", placeholder: "DG", maxLength: 4 },
];

const Field = ({ field, value, onChange }) => {
  const Common = field.textarea ? "textarea" : "input";
  return (
    <div className={field.textarea ? "col-span-full" : ""}>
      <label className="block text-[11px] font-semibold text-[#475569] uppercase tracking-wider mb-1">
        {field.label}
        {field.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <Common
        type="text"
        value={value || ""}
        onChange={e => onChange(field.k, e.target.value)}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        rows={field.textarea ? 3 : undefined}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#DC283C] focus:ring-1 focus:ring-[#DC283C]/30 bg-white"
        data-testid={`bizinfo-${field.k}`}
      />
    </div>
  );
};

const BusinessInfoCard = ({ authAxios }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type, text }

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/business-info`);
      setInfo(r.data);
    } catch {
      setMsg({ type: "error", text: "Couldn't load business info" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleChange = (k, v) => setInfo(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await authAxios.put(`${API_URL}/api/admin/business-info`, info);
      setMsg({ type: "ok", text: "Saved · invoices issued from now on use these fields" });
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ type: "error", text: e?.response?.data?.detail || "Save failed" });
    } finally { setSaving(false); }
  };

  if (loading || !info) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center" data-testid="bizinfo-loading">
        <Loader2 size={20} className="animate-spin text-[#DC283C]" />
      </div>
    );
  }

  const isFullyFilled = info.gstin && info.pan && info.cin && info.registered_address && info.authorized_signatory_name;

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" data-testid="business-info-card">
      <div className="px-5 py-4 bg-gradient-to-r from-[#1E1B4B] to-indigo-700 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Building2 size={20} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold leading-tight">Business Info · Tax Invoice Settings</h3>
          <p className="text-[11px] text-white/80 flex items-center gap-1.5">
            <FileText size={11} /> Used on every GST invoice generated for paid bookings
          </p>
        </div>
        {!isFullyFilled && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-amber-300/30 text-amber-100 font-bold flex items-center gap-1">
            <AlertCircle size={10} /> Action needed
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {!isFullyFilled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            <strong>Heads-up:</strong> Until GSTIN, PAN, CIN, registered address and signatory are filled, every invoice will print "GST registration in progress" instead of your tax-registered identity. <strong>Fill these in within 24h of going live</strong> for legal compliance.
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(f => (
            <Field key={f.k} field={f} value={info[f.k]} onChange={handleChange} />
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs">
            {msg?.type === "ok" && <span className="flex items-center gap-1 text-emerald-700"><CheckCircle size={12} /> {msg.text}</span>}
            {msg?.type === "error" && <span className="flex items-center gap-1 text-rose-600"><AlertCircle size={12} /> {msg.text}</span>}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white text-sm font-bold shadow disabled:opacity-50"
            data-testid="bizinfo-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save business info
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoCard;
